import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import { getReport, deleteReport } from '../../services/reports';

const ViewReportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getReport(id);
        if (response.success && response.report) {
          // Validate report data format
          if (response.report.report_type === 'summary' && 
              (!response.report.data || !response.report.data.summary)) {
            console.warn('Summary report missing required data structure:', response.report);
            // Add a default structure if missing
            response.report.data = {
              ...response.report.data,
              summary: {
                title: response.report.title,
                description: response.report.description || 'No description',
                findings: [],
                conclusions: []
              }
            };
          }

          // In your useEffect for loading reports
          if (response.report.report_type === 'summary') {
            // Make sure there's a data object
            response.report.data = response.report.data || {};
            
            // Make sure there's a summary object
            response.report.data.summary = response.report.data.summary || {};
            
            // Ensure findings is an array (even if empty)
            response.report.data.summary.findings = 
              response.report.data.summary.findings || 
              response.report.data.findings || 
              [];
              
            // Also directly add findings to data level for older reports
            response.report.data.findings = response.report.data.findings || 
              response.report.data.summary.findings || 
              [];
          }

          setReport(response.report);
        } else {
          setError("Failed to load report. The report might not exist or you don't have permission to view it.");
        }
      } catch (err) {
        console.error("Failed to load report", err);
        setError(`Error loading report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadReport();
    }
  }, [id]);

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const response = await deleteReport(id);
      if (response.success) {
        navigate('/statistician/reports/view');
      }
    } catch (err) {
      console.error("Failed to delete report", err);
      setError("Failed to delete report. Please try again later.");
    } finally {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const renderStatisticalReport = (data) => {
    return (
      <div className="row">
        <div className="col-md-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light py-3">
              <h6 className="mb-0">Health Metrics</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="stat-item">
                    <span className="stat-label">Average Score</span>
                    <div className="stat-value text-primary">{data.healthMetrics?.averageScore?.toFixed(1) || 'N/A'}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-item">
                    <span className="stat-label">Median Score</span>
                    <div className="stat-value text-success">{data.healthMetrics?.medianScore || 'N/A'}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-item">
                    <span className="stat-label">Standard Deviation</span>
                    <div className="stat-value text-info">{data.healthMetrics?.stdDeviation?.toFixed(2) || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light py-3">
              <h6 className="mb-0">Demographic Breakdown</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Count</th>
                      <th>Avg. Score</th>
                      <th>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.demographicBreakdown && Object.entries(data.demographicBreakdown).map(([key, value]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{value.count}</td>
                        <td>{value.averageScore?.toFixed(1) || 'N/A'}</td>
                        <td>{value.percentage?.toFixed(1) || 'N/A'}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSummaryReport = (report) => {
    // More detailed debugging
    console.log('Summary report full structure:', report);
    console.log('Report data:', report.data);
    if (report.data && report.data.summary) {
      console.log('Summary data:', report.data.summary);
    }
    if (report.data) {
      console.log('Findings in data:', report.data.findings);
      if (report.data.summary) {
        console.log('Findings in summary:', report.data.summary.findings);
      }
    }
    
    // Add null check before accessing summary property
    if (!report || !report.data) {
      return (
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2"></i>
          This summary report appears to be missing required data. It may be corrupted or incomplete.
        </div>
      );
    }

    // Check if summary data is at the top level or under summary property
    const summaryData = report.data.summary || report.data;

    // Get findings from multiple possible locations
    const findings = summaryData.findings || 
                     summaryData.highlights || 
                     summaryData.keyFindings || 
                     report.data.findings ||
                     (report.data.summary && report.data.summary.findings) || 
                     [];

    return (
      <div className="row">
        <div className="col-md-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light py-3">
              <h6 className="mb-0">Key Findings</h6>
            </div>
            <div className="card-body">
              {/* Check for overview or description */}
              <p className="lead">{summaryData.overview || summaryData.description || report.description || 'No overview available'}</p>
              <hr />
              <h6 className="text-primary">Key Findings</h6>
              {/* Check multiple possible locations for findings data */}
              {Array.isArray(findings) && findings.length > 0 ? (
                <ul className="list-unstyled">
                  {findings.map((item, index) => (
                    <li key={index} className="mb-2">
                      <i className="fa fa-check-circle text-success me-2"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">No key findings available</p>
              )}
              
              {/* Check for conclusions if they exist */}
              {(summaryData.conclusions || summaryData.recommendations || []).length > 0 && (
                <>
                  <hr />
                  <h6 className="text-primary">Conclusions</h6>
                  <ul className="list-unstyled">
                    {(summaryData.conclusions || summaryData.recommendations || []).map((item, index) => (
                      <li key={index} className="mb-2">
                        <i className="fa fa-lightbulb text-warning me-2"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Trends section - check if it exists at various possible locations */}
        {(report.data.trends || summaryData.trends) && (
          <div className="col-md-12 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light py-3">
                <h6 className="mb-0">Trends</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  {Object.entries(report.data.trends || summaryData.trends || {}).map(([key, value]) => (
                    <div className="col-md-4 mb-3" key={key}>
                      <div className="p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{key}</h6>
                          <span className={`badge ${value.trend === 'up' ? 'bg-success' : value.trend === 'down' ? 'bg-danger' : 'bg-secondary'}`}>
                            <i className={`fa fa-arrow-${value.trend}`}></i> {value.percentage}%
                          </span>
                        </div>
                        <div className="progress" style={{height: '10px'}}>
                          <div
                            className={`progress-bar bg-${value.trend === 'up' ? 'success' : value.trend === 'down' ? 'danger' : 'secondary'}`}
                            role="progressbar"
                            style={{width: `${value.percentage}%`}}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAnalysisReport = (report) => {
    // Add null check before accessing analysis property
    if (!report || !report.data || !report.data.analysis) {
      return (
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2"></i>
          This analysis report appears to be missing required data. It may be corrupted or incomplete.
        </div>
      );
    }

    const { analysis } = report.data;

    return (
      <div className="row">
        <div className="col-md-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light py-3">
              <h6 className="mb-0">Analysis Summary</h6>
            </div>
            <div className="card-body">
              <p className="lead">{analysis.summary || 'No analysis summary available'}</p>
            </div>
          </div>
        </div>

        {analysis.sections && analysis.sections.map((section, index) => (
          <div className="col-md-12 mb-4" key={index}>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light py-3">
                <h6 className="mb-0">{section.title || `Section ${index + 1}`}</h6>
              </div>
              <div className="card-body">
                <p>{section.content || 'No content available for this section'}</p>
                
                {section.points && section.points.length > 0 && (
                  <div className="mt-3">
                    <h6 className="text-primary">Key Points</h6>
                    <ul className="list-group list-group-flush">
                      {section.points.map((point, i) => (
                        <li key={i} className="list-group-item border-0">
                          <i className="fa fa-check text-success me-2"></i>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {analysis.conclusion && (
          <div className="col-md-12 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light py-3">
                <h6 className="mb-0">Conclusion</h6>
              </div>
              <div className="card-body">
                <p>{analysis.conclusion}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDashboardReport = (report) => {
    // Add null check before accessing dashboard property
    if (!report || !report.data || !report.data.dashboard) {
      return (
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2"></i>
          This dashboard report appears to be missing required data. It may be corrupted or incomplete.
        </div>
      );
    }

    const { dashboard } = report.data;

    return (
      <div className="row">
        {/* Summary Cards */}
        <div className="col-md-12 mb-4">
          <div className="row">
            {dashboard.summaryCards && dashboard.summaryCards.map((card, index) => (
              <div className="col-md-3 mb-4" key={index}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="text-muted mb-0">{card.title}</h6>
                      <i className={`fa fa-${card.icon || 'chart-simple'} text-${card.color || 'primary'}`}></i>
                    </div>
                    <div className="stat-value text-${card.color || 'primary'} my-3">
                      {card.value}
                      {card.unit && <span className="ms-1 small">{card.unit}</span>}
                    </div>
                    {card.change && (
                      <div className={`small ${card.change > 0 ? 'text-success' : 'text-danger'}`}>
                        <i className={`fa fa-arrow-${card.change > 0 ? 'up' : 'down'} me-1`}></i>
                        {Math.abs(card.change)}% from previous
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts Section */}
        {dashboard.charts && dashboard.charts.map((chart, index) => (
          <div className={`col-md-${chart.width || 6} mb-4`} key={index}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-light py-3">
                <h6 className="mb-0">{chart.title}</h6>
              </div>
              <div className="card-body">
                {/* Here we'd normally render a chart, but as a placeholder: */}
                <div className="text-center py-5 bg-light rounded">
                  <i className="fa fa-chart-bar fa-3x text-primary mb-3"></i>
                  <p className="mb-0">
                    {chart.description || 'Chart visualization would appear here'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Data Tables */}
        {dashboard.tables && dashboard.tables.map((table, index) => (
          <div className="col-md-12 mb-4" key={index}>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light py-3">
                <h6 className="mb-0">{table.title}</h6>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        {table.headers && table.headers.map((header, i) => (
                          <th key={i}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows && table.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderReportContent = (report) => {
    if (!report) {
      return (
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-circle me-2"></i>
          Report data could not be loaded.
        </div>
      );
    }

    try {
      switch (report.report_type) {
        case 'summary':
          return renderSummaryReport(report);
        case 'statistical':
          return renderStatisticalReport(report);
        case 'analysis':
          return renderAnalysisReport(report);
        case 'dashboard':
          return renderDashboardReport(report);
        default:
          return (
            <div className="alert alert-info">
              <i className="fa fa-info-circle me-2"></i>
              Unknown report type: {report.report_type}
            </div>
          );
      }
    } catch (error) {
      console.error('Error rendering report:', error);
      return (
        <div className="alert alert-danger">
          <i className="fa fa-bug me-2"></i>
          An error occurred while rendering this report: {error.message}
          <div className="mt-2 small">
            Please contact support if this issue persists.
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="View Report">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading report...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (error) {
    return (
      <ResearchLayout pageTitle="View Report">
        <div className="container-fluid">
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
          <div className="text-center">
            <Link to="/statistician/reports/view" className="btn btn-primary">
              <i className="fa fa-arrow-left me-2"></i>
              Back to Reports
            </Link>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (!report) {
    return (
      <ResearchLayout pageTitle="View Report">
        <div className="container-fluid">
          <div className="alert alert-warning">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Report not found
          </div>
          <div className="text-center">
            <Link to="/statistician/reports/view" className="btn btn-primary">
              <i className="fa fa-arrow-left me-2"></i>
              Back to Reports
            </Link>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle={`Report: ${report.title}`}>
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">{report.title}</h3>
                    <p className="mb-0 opacity-75">
                      {report.description || 'No description provided'}
                    </p>
                  </div>
                  <div>
                    <Link to="/statistician/reports/view" className="btn btn-outline-light me-2">
                      <i className="fa fa-arrow-left me-2"></i>
                      Back
                    </Link>
                    <div className="btn-group">
                      <Link to={`/statistician/reports/edit/${report.id}`} className="btn btn-light">
                        <i className="fa fa-edit me-2"></i>
                        Edit
                      </Link>
                      <button className="btn btn-danger" onClick={handleDeleteClick}>
                        <i className="fa fa-trash-alt me-2"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 mb-3 mb-md-0">
                    <span className="text-muted d-block">Report Type</span>
                    <span className="badge bg-info">
                      {report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)}
                    </span>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <span className="text-muted d-block">Created</span>
                    <span>{new Date(report.created_at).toLocaleString()}</span>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <span className="text-muted d-block">Created By</span>
                    <span>{report.creator_name || 'Unknown'}</span>
                  </div>
                  <div className="col-md-3">
                    <span className="text-muted d-block">Last Updated</span>
                    <span>{new Date(report.updated_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="mb-0">Report Filters</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 mb-3 mb-md-0">
                    <span className="text-muted d-block">Date Range</span>
                    <span>{report.filters?.dateRange || 'All Time'}</span>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <span className="text-muted d-block">Score Range</span>
                    <span>
                      {report.filters?.minScore || '0'} - {report.filters?.maxScore || '100'}
                    </span>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <span className="text-muted d-block">Age Group</span>
                    <span>{report.filters?.ageGroup || 'All Ages'}</span>
                  </div>
                  <div className="col-md-3">
                    <span className="text-muted d-block">Gender</span>
                    <span>{report.filters?.gender ? report.filters.gender.charAt(0).toUpperCase() + report.filters.gender.slice(1) : 'All'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {renderReportContent(report)}
      </div>
      
      {deleteModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Report</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setDeleteModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this report? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-trash-alt me-2"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .stat-item {
          text-align: center;
          padding: 1rem;
        }
        
        .stat-label {
          display: block;
          font-size: 0.875rem;
          color: #6c757d;
          margin-bottom: 0.5rem;
        }
        
        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
        }
      `}</style>
    </ResearchLayout>
  );
};

export default ViewReportDetailPage;